import React, { useState } from 'react';
import JobsModule from '../components/production/JobsModule';
import { CalendarModule } from '../components/production/CalendarModule';
import SepticAsBuiltModule from '../components/production/SepticAsBuiltModule';
import SumpPumpInspectionModule from '../components/production/SumpPumpModule';
import VehicleInspections from '../components/production/VehicleInspections';
import EmployeeContactsModule from '../components/production/EmployeeContactsModule';
import ChecklistsModule from '../components/production/ChecklistsModule';
import VendorsModule from '../components/production/VendorsModule';
import PierLogsModule from '../components/production/PierLogsModule';
import InstallationManualsModule from '../components/production/InstallationManualsModule';
import SafetyModule from '../components/production/SafetyModule';

const ProductionHomePage = ({ onNavigate, onBackToGears, currentUser }) => {
  const enabledTools = currentUser?.company?.tools_enabled?.production;
  const isToolEnabled = (toolId) => !enabledTools || enabledTools.includes(toolId);

  const toolCards = [
    { id: 'jobs', icon: '📋', title: 'JOBS', desc: 'Manage jobs, forms & documentation' },
    { id: 'safety', icon: '⚠️', title: 'SAFETY', desc: 'Emergency procedures & protocols', badge: 'EMERGENCY' },
    { id: 'vehicle-inspections', icon: '🚛', title: 'VEHICLE INSPECTIONS', desc: 'Daily vehicle & trailer inspections' },
    { id: 'calendar', icon: '📅', title: 'CALENDAR', desc: 'Schedule jobs & manage crew assignments' },
    { id: 'manuals', icon: '📚', title: 'INSTALLATION MANUALS', desc: 'Access & manage technical documentation' },
    { id: 'checklists', icon: '✅', title: 'CHECKLISTS', desc: 'Safety protocols & work checklists' },
    { id: 'vendors', icon: '🏪', title: 'VENDOR LIST', desc: 'Manage suppliers & vendors' },
    { id: 'pier-logs', icon: '📊', title: 'PIER LOGS', desc: 'Track pier installation data & progress' },
    { id: 'sump-pump-inspections', icon: '💧', title: 'SUMP PUMP INSPECTIONS', desc: 'Step-by-step sump pump inspection form' },
    { id: 'septic-as-built', icon: '🧭', title: 'SEPTIC AS BUILT', desc: 'As-built triangulation & elevation tables' },
    { id: 'employees', icon: '👷', title: 'EMPLOYEE CONTACTS', desc: 'Find staff contact information' },
  ].filter(t => isToolEnabled(t.id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Back to Gears Button */}
        <div className="mb-6">
          <button 
            onClick={onBackToGears}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Company Gears
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center mb-6">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
              🏗️ PRODUCTION
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-gray-600 mb-2">Field Operations & Job Management</p>
          <p className="text-gray-500">Staff Access - Documentation & Resource Management</p>
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {toolCards.map(card => (
            <div
              key={card.id}
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => onNavigate(card.id)}
              data-testid={`production-tool-${card.id}`}
            >
              <div className="text-center">
                {card.badge && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-xs mb-2 inline-block font-bold">{card.badge}</div>
                )}
                <div className="text-6xl mb-4">{card.icon}</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">{card.title}</h2>
                <p className="text-gray-600 text-sm">{card.desc}</p>
              </div>
            </div>
          ))}
          {toolCards.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-400">
              No tools are enabled for your company in this module.
            </div>
          )}
        </div>

        {/* System Status Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            🟢 Production Systems Operational | Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
        </div>
      </div>
  );
};

// Coming Soon Page for other gears


const ProductionGear = ({ currentUser, onBackToGears }) => {
  const [currentPage, setCurrentPage] = useState('home');
  const [jobToEditFromCalendar, setJobToEditFromCalendar] = useState(null);

  switch (currentPage) {
    case 'home':
      return <ProductionHomePage onNavigate={setCurrentPage} onBackToGears={onBackToGears} currentUser={currentUser} />;
    case 'jobs':
      return (
        <JobsModule
          onBack={() => setCurrentPage('home')}
          currentUser={currentUser}
          jobToEdit={jobToEditFromCalendar}
          setJobToEdit={setJobToEditFromCalendar}
          onJobEditComplete={() => {
            setCurrentPage('calendar');
            setJobToEditFromCalendar(null);
          }}
        />
      );
    case 'vehicle-inspections':
      return <VehicleInspections onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'calendar':
      return (
        <CalendarModule
          onBack={() => setCurrentPage('home')}
          currentUser={currentUser}
          onEditJob={(job) => {
            setJobToEditFromCalendar(job);
            setCurrentPage('jobs');
          }}
          initialDate={null}
        />
      );
    case 'employees':
      return <EmployeeContactsModule onBack={() => setCurrentPage('home')} />;
    case 'septic-as-built':
      return <SepticAsBuiltModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'vendors':
      return <VendorsModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'manuals':
      return <InstallationManualsModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'checklists':
      return <ChecklistsModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'safety':
      return <SafetyModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'pier-logs':
      return <PierLogsModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    case 'sump-pump-inspections':
      return <SumpPumpInspectionModule onBack={() => setCurrentPage('home')} currentUser={currentUser} />;
    default:
      return <ProductionHomePage onNavigate={setCurrentPage} onBackToGears={onBackToGears} currentUser={currentUser} />;
  }
};

export default ProductionGear;
